import { useIsFocused } from "@react-navigation/native";
import { useContext, useEffect, useState } from "react";
import { ScrollView, StyleSheet, Text, View } from "react-native";
import Icon from "react-native-vector-icons/MaterialCommunityIcons";
import Button from "../../components/Button";
import PageTitle from "../../components/PageTitle";
import { GlobalContext } from "../../contexts/global";
import {
  activateUser,
  deactivateUser,
  deleteUser,
  getUserDetails,
} from "../../services/Api/admin";
import * as Colors from "../../styles/colors";
import * as Fonts from "../../styles/typography";

export default function AdminUserDetail({ navigation, route }) {
  const { userId } = route.params;
  const { setLoading, setShowAlert, showAlert } = useContext(GlobalContext);
  const [user, setUser] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const isFocused = useIsFocused();

  useEffect(() => {
    setLoading({
      show: true,
      message: "Carregando dados do usuário",
    });
  }, []);

  useEffect(() => {
    if (isFocused) {
      loadUserDetails();
    }
  }, [isFocused]);

  const loadUserDetails = () => {
    getUserDetails(userId)
      .then((response) => {
        setUser(response);
      })
      .catch((error) => {
        const message =
          error?.message || error?.erro || "Erro ao carregar dados do usuário";
        setShowAlert({
          type: "alert",
          title: "Erro",
          message: message,
          show: true,
          confirmText: "OK",
          pressConfirm: () => {
            setShowAlert({ ...showAlert, show: false });
            navigation.goBack();
          },
        });
      })
      .finally(() => {
        setLoading({ show: false, message: "" });
      });
  };


  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleActivate = () => {
    setActionLoading(true);
    setLoading({ show: true, message: "Ativando usuário" });

    activateUser(userId)
      .then((response) => {
        setUser((prev) => ({ ...prev, active: true }));
        setShowAlert({
          type: "success",
          title: "Sucesso",
          message: response?.message || "Usuário ativado com sucesso",
          show: true,
          confirmText: "OK",
          pressConfirm: () => setShowAlert({ ...showAlert, show: false }),
        });
      })
      .catch((error) => {
        const message =
          error?.message || error?.erro || "Erro ao ativar usuário";
        setShowAlert({
          type: "alert",
          title: "Erro",
          message: message,
          show: true,
          confirmText: "OK",
          pressConfirm: () => setShowAlert({ ...showAlert, show: false }),
        });
      })
      .finally(() => {
        setActionLoading(false);
        setLoading({ show: false, message: "" });
      });
  };

  const handleDeactivate = () => {
    setActionLoading(true);
    setLoading({ show: true, message: "Desativando usuário" });

    deactivateUser(userId)
      .then((response) => {
        setUser((prev) => ({ ...prev, active: false }));
        setShowAlert({
          type: "success",
          title: "Sucesso",
          message: response?.message || "Usuário desativado com sucesso",
          show: true,
          confirmText: "OK",
          pressConfirm: () => setShowAlert({ ...showAlert, show: false }),
        });
      })
      .catch((error) => {
        const message =
          error?.message || error?.erro || "Erro ao desativar usuário";
        setShowAlert({
          type: "alert",
          title: "Erro",
          message: message,
          show: true,
          confirmText: "OK",
          pressConfirm: () => setShowAlert({ ...showAlert, show: false }),
        });
      })
      .finally(() => {
        setActionLoading(false);
        setLoading({ show: false, message: "" });
      });
  };


  const handleDeleteConfirm = () => {
    setShowAlert({
      type: "warning",
      title: "Confirmar Exclusão",
      message: `Tem certeza que deseja excluir o usuário "${user?.name}"? Esta ação não pode ser desfeita.`,
      show: true,
      cancelText: "Cancelar",
      confirmText: "Excluir",
      pressCancel: () => setShowAlert({ ...showAlert, show: false }),
      pressConfirm: () => {
        setShowAlert({ ...showAlert, show: false });
        handleDelete();
      },
    });
  };

  const handleDelete = () => {
    setActionLoading(true);
    setLoading({ show: true, message: "Excluindo usuário" });

    deleteUser(userId)
      .then((response) => {
        setShowAlert({
          type: "success",
          title: "Sucesso",
          message: response?.message || "Usuário excluído com sucesso",
          show: true,
          confirmText: "OK",
          pressConfirm: () => {
            setShowAlert({ ...showAlert, show: false });
            navigation.goBack();
          },
        });
      })
      .catch((error) => {
        const message =
          error?.message || error?.erro || "Erro ao excluir usuário";
        setShowAlert({
          type: "alert",
          title: "Erro",
          message: message,
          show: true,
          confirmText: "OK",
          pressConfirm: () => setShowAlert({ ...showAlert, show: false }),
        });
      })
      .finally(() => {
        setActionLoading(false);
        setLoading({ show: false, message: "" });
      });
  };

  const handleAdjustWallet = () => {
    navigation.navigate("AdminWalletAdjust", {
      userId: user.id,
      userName: user.name,
      currentBalance: user.walletBalance,
    });
  };

  const handleViewTransactions = () => {
    navigation.navigate("AdminUserTransactions", {
      userId: user.id,
      userName: user.name,
      userEmail: user.email,
    });
  };


  const renderInfoRow = (iconName, label, value) => (
    <View style={styles.infoRow}>
      <Icon name={iconName} size={20} color={Colors.PRIMARY} />
      <View style={styles.infoContent}>
        <Text style={styles.infoLabel}>{label}</Text>
        <Text style={styles.infoValue}>{value || "N/A"}</Text>
      </View>
    </View>
  );

  if (!user) {
    return (
      <PageTitle title={"Detalhes do Usuário"} navigation={navigation}>
        <View style={styles.container} />
      </PageTitle>
    );
  }

  return (
    <PageTitle title={"Detalhes do Usuário"} navigation={navigation}>
      <ScrollView style={styles.container}>
        {/* User Header */}
        <View style={styles.header}>
          <Icon name="account-circle" size={80} color={Colors.PRIMARY} />
          <Text style={styles.userName}>{user.name}</Text>
          <View
            style={[
              styles.statusBadge,
              { backgroundColor: user.active ? Colors.SUCCESS : Colors.ALERT },
            ]}
          >
            <Text style={styles.statusText}>
              {user.active ? "Ativo" : "Inativo"}
            </Text>
          </View>
        </View>

        {/* User Information */}
        <View style={styles.infoSection}>
          <Text style={styles.sectionTitle}>Informações do Usuário</Text>
          {renderInfoRow("email-outline", "Email", user.email)}
          {renderInfoRow("phone-outline", "Telefone", user.phone)}
          {renderInfoRow("card-account-details-outline", "Documento", user.document)}
          {renderInfoRow("calendar-outline", "Data de Criação", formatDate(user.dtCreated))}
        </View>

        {/* Wallet Information */}
        <View style={styles.walletSection}>
          <View style={styles.walletHeader}>
            <Icon name="wallet" size={24} color={Colors.WALLET} />
            <Text style={styles.walletLabel}>Saldo da Carteira</Text>
          </View>
          <Text style={styles.walletBalance}>
            R$ {(user.walletBalance || 0).toFixed(2)}
          </Text>
        </View>


        {/* Action Buttons */}
        <View style={styles.actionsSection}>
          <Text style={styles.sectionTitle}>Ações</Text>

          {/* Activate/Deactivate Button */}
          {user.active ? (
            <Button
              textoBotao="Desativar Usuário"
              estilo={styles.deactivateButton}
              onPress={handleDeactivate}
              disabled={actionLoading}
            />
          ) : (
            <Button
              textoBotao="Ativar Usuário"
              estilo={styles.activateButton}
              onPress={handleActivate}
              disabled={actionLoading}
            />
          )}

          {/* Adjust Wallet Button */}
          <Button
            textoBotao="Ajustar Carteira"
            estilo={styles.walletButton}
            onPress={handleAdjustWallet}
            disabled={actionLoading}
          />

          {/* View Transactions Button */}
          <Button
            textoBotao="Ver Transações"
            estilo={styles.transactionsButton}
            onPress={handleViewTransactions}
            disabled={actionLoading}
          />

          {/* Delete Button */}
          <Button
            textoBotao="Excluir Usuário"
            estilo={styles.deleteButton}
            onPress={handleDeleteConfirm}
            disabled={actionLoading}
          />
        </View>
      </ScrollView>
    </PageTitle>
  );
}


const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.WHITE,
  },
  header: {
    alignItems: "center",
    paddingVertical: 24,
    borderBottomWidth: 1,
    borderBottomColor: Colors.GRAY_LIGHT,
  },
  userName: {
    fontSize: Fonts.FONT_SIZE_22,
    fontWeight: "bold",
    color: Colors.BLACK,
    marginTop: 8,
    marginBottom: 8,
  },
  statusBadge: {
    paddingHorizontal: 16,
    paddingVertical: 4,
    borderRadius: 16,
  },
  statusText: {
    fontSize: Fonts.FONT_SIZE_14,
    color: Colors.WHITE,
    fontWeight: "bold",
  },
  infoSection: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: Colors.GRAY_LIGHT,
  },
  sectionTitle: {
    fontSize: Fonts.FONT_SIZE_16,
    fontWeight: "bold",
    color: Colors.BLACK,
    marginBottom: 16,
  },
  infoRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 12,
  },
  infoContent: {
    marginLeft: 12,
    flex: 1,
  },
  infoLabel: {
    fontSize: Fonts.FONT_SIZE_12,
    color: Colors.GRAY_DARK,
  },
  infoValue: {
    fontSize: Fonts.FONT_SIZE_16,
    color: Colors.BLACK,
  },
  walletSection: {
    padding: 16,
    backgroundColor: Colors.SNOW,
    marginHorizontal: 16,
    marginVertical: 16,
    borderRadius: 8,
    alignItems: "center",
  },
  walletHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 8,
  },
  walletLabel: {
    fontSize: Fonts.FONT_SIZE_16,
    color: Colors.WALLET,
    marginLeft: 8,
    fontWeight: "bold",
  },
  walletBalance: {
    fontSize: Fonts.FONT_SIZE_28,
    color: Colors.WALLET,
    fontWeight: "bold",
  },
  actionsSection: {
    padding: 16,
  },
  activateButton: {
    backgroundColor: Colors.SUCCESS,
    marginBottom: 12,
  },
  deactivateButton: {
    backgroundColor: Colors.WARNING,
    marginBottom: 12,
  },
  walletButton: {
    backgroundColor: Colors.INFO,
    marginBottom: 12,
  },
  transactionsButton: {
    backgroundColor: Colors.PRIMARY,
    marginBottom: 12,
  },
  deleteButton: {
    backgroundColor: Colors.ALERT,
    marginBottom: 12,
  },
});
